import {memo, useCallback, useState} from 'react';
import {loadStripe} from '@stripe/stripe-js';
import {
	Elements,
	useStripe,
	useElements,
	CardNumberElement,
	CardCvcElement,
	CardExpiryElement,
} from '@stripe/react-stripe-js';
import Image from 'next/image';

import {api} from '@utils/api';
import AgeVerify from '@components/checkout/blocks/AgeVerify';
import MessageComponent from '@components/common/MessageComponent';
import Loading from '@components/Loading';
const stripePromise = loadStripe(process.env.MIX_STRIPE_KEY);
const options = {
	fonts: [
		{
			cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans',
		},
	],
};

const Form = props => {
	const {
		styles,
		item,
		type,
		user,
		session,
		editCard,
		loadCards,
		count,
		isGuest = false,
		loading = false,
		disableBtn = false,
		checkoutAsGuest = false,
		is18Active = false,
		isIdentityError = false,
	} = props;
	const stripe = useStripe();
	const elements = useElements();
	const [card, setCard] = useState({
		submitted: false,
		futureReference: false,
		error: null,
		loading: false,
		cardPayload: {},
		...item,
	});

	const [alcoholicData, setAlcoholicData] = useState({});
	const updateAgeData = useCallback(obj => {
		console.log({obj});
		setAlcoholicData(prevState => ({
			...prevState,
			...obj,
		}));
	}, []);

	const handleChange = event => {
		const {name, value, type, checked} = event.target;
		setCard(val => ({
			...val,
			[name]: type === 'checkbox' ? checked : value,
		}));
	};

	const validateCard = useCallback(() => {
		let err = {};
		if (!card.name) {
			err['name'] = 'Enter name.';
		} else if (!isNaN(card.name)) {
			err['name'] = 'Invalid name.';
		}

		if (!card.exp_month) {
			err['exp_month'] = 'Month cannot be empty.';
		} else if (card.exp_month < 1 || card.exp_month > 12) {
			err['exp_month'] = 'Enter valid month.';
		}

		if (!card.exp_year) {
			err['exp_year'] = 'Year cannot be empty.';
		} else if (card.exp_year < new Date().getFullYear()) {
			err['exp_year'] = 'Enter valid year.';
		}

		return err;
	}, [card]);

	const handleSubmit = useCallback(
		async event => {
			event.preventDefault();
			if (
				(isGuest === true || checkoutAsGuest === true) &&
				Object.keys(card.cardPayload).length > 0
			) {
				loadCards(card.cardPayload, alcoholicData);
				return;
			}
			if (type === 'edit') {
				let error = validateCard();

				if (Object.keys(error).length === 0) {
					setCard(val => ({...val, error: null, loading: true}));
					const result = await api(
						{
							url: `/user/updateCard`,
							method: 'post',
							data: {
								// eslint-disable-next-line camelcase
								stripe_id: user.stripe_customer_id,
								source: card.id,
								name: card.name,
								year: card.exp_year,
								month: card.exp_month,
								futureReference: card.futureReference ? true : false,
							},
						},
						session,
					);
					if (result.status) {
						if (card.new) {
							loadCards({...result.data, new: true});
						} else {
							loadCards();
						}
					} else {
						setCard(val => ({
							...val,
							error: {commonError: result?.response?.data?.errors?.message},
							loading: false,
						}));
					}
				} else {
					setCard(val => ({...val, error}));
				}
			} else {
				if (!stripe || !elements) {
					return;
				}

				let error = {};
				if (!card.name) {
					error['name'] = 'Enter name.';
				} else if (!isNaN(card.name)) {
					error['name'] = 'Invalid name.';
				}

				const payload = await stripe.createToken(
					elements.getElement(CardNumberElement),
					{
						card: {name: card.name},
					},
				);

				error = {...error, ...payload.error};

				if (Object.keys(error).length) {
					setCard(val => ({...val, error: error, submitted: true}));
					if (isGuest === true || checkoutAsGuest === true) {
						loadCards(null, alcoholicData);
					}
				} else {
					if (isGuest === true || checkoutAsGuest === true) {
						setCard(val => ({
							...val,
							error: null,
							cardPayload: payload,
							submitted: true,
						}));
						loadCards(payload, alcoholicData);
					} else {
						setCard(val => ({...val, error: null, loading: true}));
						const result = await api(
							{
								url: `/user/addNewCard`,
								method: 'post',
								data: {
									// eslint-disable-next-line camelcase
									stripe_id: user.stripe_customer_id,
									source: payload.token.id,
									name: card?.name,
									futureReference: card.futureReference ? true : false,
								},
							},
							session,
						);
						if (result.status) {
							loadCards({...result.data, new: true});
						} else {
							setCard(val => ({
								...val,
								error: {commonError: result?.response?.data?.errors?.message},
								loading: false,
							}));
						}
					}
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			type,
			validateCard,
			user?.stripe_customer_id,
			card.id,
			card.name,
			card.exp_year,
			card.exp_month,
			card.futureReference,
			card.new,
			session,
			loadCards,
			stripe,
			elements,
			isGuest,
			alcoholicData,
		],
	);
	return (
		<form onSubmit={handleSubmit}>
			{count > 0 && (
				<div className={styles.closeBtn}>
					<Image
						src='/assets/images/close-icon-red.svg'
						alt='close-icon'
						onClick={() => editCard(null)}
						fill
						sizes='100vw'
					/>
				</div>
			)}
			<div className={styles.paymentDetailsForm}>
				<div className='form-group'>
					<input
						type='text'
						className='form-control'
						placeholder="Cardholder's name"
						name='name'
						defaultValue={type === 'edit' ? card.name : ''}
						onChange={e => handleChange(e)}
					/>
					{card.error && card.error.name && (
						<MessageComponent
							messageKey={'errorText'}
							message={card.error.name}
						>
							{' '}
						</MessageComponent>
					)}
				</div>
				<div className='form-group'>
					{type === 'edit' ? (
						<input
							type='text'
							className={`form-control`}
							placeholder={`**** **** **** ${card.last4}`}
							disabled
						/>
					) : (
						<CardNumberElement
							options={{placeholder: 'Card Number'}}
							style={{
								fontSize: '14px',
								fontWeight: '500',
								fontFamily: 'Poppins',
								lineHeight: '50px',
								height: '50px',
								border: 'solid 1px #f04f24',
							}}
							className={`form-control ${styles.cardElementInput}  `}
						/>
					)}
					{card.error &&
						(card.error.code === 'incomplete_number' ||
							card.error.code === 'invalid_number') && (
							<MessageComponent
								messageKey={'errorText'}
								message={card.error.message}
							>
								{' '}
							</MessageComponent>
						)}
				</div>
				<div className='form-group d-flex justify-content-between row'>
					{type === 'edit' ? (
						<>
							<div className='col-md-6 mb-3'>
								<input
									type='text'
									className='form-control'
									placeholder='MM'
									name='exp_month'
									value={card.exp_month}
									onChange={handleChange}
								/>
								{card.error && card.error.exp_month && (
									<MessageComponent
										messageKey={'errorText'}
										message={card.error.exp_month}
									>
										{' '}
									</MessageComponent>
								)}
							</div>
							<div className='col-md-6 mb-3'>
								<input
									type='text'
									className='form-control'
									placeholder='YY'
									name='exp_year'
									value={card.exp_year}
									onChange={handleChange}
								/>
								{card.error && card.error.exp_year && (
									<MessageComponent
										messageKey={'errorText'}
										message={card.error.exp_year}
									>
										{' '}
									</MessageComponent>
								)}
							</div>
						</>
					) : (
						<>
							<div className='col-md-6 mb-3'>
								<CardExpiryElement
									options={{placeholder: 'Exp MM/YY'}}
									className={`form-control ${styles.cardElementInput} `}
								/>
								{card.error &&
									(card.error.code === 'incomplete_expiry' ||
										card.error.code === 'invalid_expiry_year_past') && (
										<MessageComponent
											messageKey={'errorText'}
											message={card.error.message}
										>
											{' '}
										</MessageComponent>
									)}
							</div>
							<div className='col-md-6 mb-3'>
								<CardCvcElement
									options={{placeholder: 'CVV'}}
									className={`form-control ${styles.cardElementInput}`}
								/>
								{card.error && card.error.code === 'incomplete_cvc' && (
									<MessageComponent
										messageKey={'errorText'}
										message={card.error.message}
									>
										{' '}
									</MessageComponent>
								)}
							</div>
						</>
					)}
				</div>
			</div>
			{is18Active && (
				<AgeVerify
					callBack={updateAgeData}
					is18Active={is18Active}
					isFormSubmitted={card.submitted}
					state={{...alcoholicData, isIdentityError}}
				/>
			)}
			{isGuest === true ? (
				<div className={styles.guestBtn}>
					<button
						type='submit'
						className={`btnCustom  peachBtn ${
							disableBtn === true ? 'disableBtn' : ''
						}`}
						disabled={card.loading || loading || disableBtn}
					>
						<span>Enter as a guest</span>
						{(card.loading || loading) && <Loading.LoaderThree />}
					</button>
				</div>
			) : (
				<button
					type='submit'
					className='btnCustom mossBtn'
					disabled={card.loading}
				>
					<span>{`${type === 'edit' ? 'Update' : 'Add'} Payment Method`}</span>
					{card.loading && <Loading.LoaderThree />}
				</button>
			)}
			{card.error && card.error.commonError && (
				<div className={styles.mgTop10}>
					<MessageComponent
						messageKey={'error'}
						message={card.error.commonError}
					>
						{' '}
					</MessageComponent>
				</div>
			)}
			{type !== 'edit' && !isGuest && !checkoutAsGuest && (
				<div className='mt-3'>
					<label className='custom-check'>
						Remember for future purchases
						<input
							type='checkbox'
							name='futureReference'
							checked={card.futureReference}
							onChange={handleChange}
						/>
						<span className={`${styles.checkmark} checkmark`}></span>
					</label>
				</div>
			)}
		</form>
	);
};

const El = props => {
	return (
		<Elements stripe={stripePromise} options={options}>
			<Form {...props} />
		</Elements>
	);
};

export default memo(El);
